import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { can, peekPermission } from './can';
import { expirePermission, permissionKey, subscribe } from './permissionCache';

export interface UseCanResult {
  /** `undefined` while unresolved — callers must not flash the control (§14.5). */
  allowed: boolean | undefined;
  isLoading: boolean;
  /** Cache-busting re-evaluation (the "Try again" path for a failed check). */
  refetch: () => void;
}

/**
 * Policy verdict for one `(action, resource)` pair (design §14.2).
 *
 * Identical pairs mounted anywhere on a page resolve exactly once: the store
 * snapshot is shared and the in-flight map de-duplicates the request (§14.3).
 * Fail-closed: an evaluation error settles as `allowed: false` (short TTL, so a
 * transient outage does not lock the UI out for five minutes).
 */
export function useCan(action: string, resource: string): UseCanResult {
  const key = permissionKey(action, resource);

  const getSnapshot = useCallback(() => peekPermission(action, resource), [action, resource]);
  const entry = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (entry) {
      return;
    }
    void can(action, resource);
  }, [action, resource, entry]);

  const refetch = useCallback(() => {
    expirePermission(key);
    void can(action, resource);
  }, [action, resource, key]);

  return {
    allowed: entry?.allowed,
    isLoading: entry === undefined,
    refetch,
  };
}
